import React, { useState, useEffect } from 'react';
import './Admin.css';

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
function getToken() {
  try {
    const s = sessionStorage.getItem('supabase-auth-token');
    return s ? (JSON.parse(s).access_token ?? process.env.REACT_APP_SUPABASE_ANON_KEY) : process.env.REACT_APP_SUPABASE_ANON_KEY;
  } catch { return process.env.REACT_APP_SUPABASE_ANON_KEY; }
}

function apiFetch(path) {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return fetch(`${url}/rest/v1/${path}`, {
    headers: { apikey: key, Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
  });
}

/* ──────────────────────────────────────────────
   Quick-action card
────────────────────────────────────────────── */
function ActionCard({ icon, title, desc, btnLabel, btnVariant = 'outline', onClick, disabled }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid rgba(0,107,84,0.1)',
      borderRadius: 14,
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      boxShadow: '0 1px 3px rgba(7,39,30,0.05)',
      transition: 'box-shadow 0.2s',
    }}>
      <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(0,107,84,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`bi ${icon}`} style={{ fontSize: '1.2rem', color: '#006B54' }} />
      </div>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#15211d' }}>{title}</div>
      <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0, lineHeight: 1.5, flex: 1 }}>{desc}</p>
      <button className={`adm-btn adm-btn--${btnVariant} adm-btn--sm`} onClick={onClick} disabled={disabled}>
        {btnLabel}
      </button>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Main Component
────────────────────────────────────────────── */
const SupportActions = () => {
  const [tab, setTab]           = useState('actions');
  const [users, setUsers]       = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [impersonateEmail, setImpersonateEmail] = useState('');
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateMsg, setImpersonateMsg] = useState('');

  const [resetEmail, setResetEmail] = useState('');
  const [resetting, setResetting]   = useState(false);
  const [resetMsg, setResetMsg]     = useState('');

  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  useEffect(() => {
    if (tab !== 'impersonate') return;
    setLoadingUsers(true);
    apiFetch('profiles?select=id,email,first_name,last_name,role,organization_id&order=email.asc')
      .then(r => r.ok ? r.json() : [])
      .then(data => setUsers(data))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [tab]);

  const handleImpersonate = async () => {
    if (!impersonateEmail.trim()) return;
    if (!impersonateReason.trim()) { setImpersonateMsg('Please provide a reason for impersonation.'); return; }
    setImpersonating(true);
    setImpersonateMsg('');
    /* Log impersonation to audit_logs if it exists */
    try {
      const url = process.env.REACT_APP_SUPABASE_URL;
      const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
      await fetch(`${url}/rest/v1/audit_logs`, {
        method: 'POST',
        headers: {
          apikey: key,
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        },
        body: JSON.stringify({
          action:  'impersonate',
          actor:   'platform_admin',
          target:  impersonateEmail,
          reason:  impersonateReason,
          created_at: new Date().toISOString(),
        }),
      });
    } catch { /* Audit log table may not exist yet */ }
    setImpersonating(false);
    setImpersonateEmail('');
    setImpersonateReason('');
    showToast('Impersonation session initiated and logged.');
  };

  const handlePasswordReset = async () => {
    if (!resetEmail.trim()) return;
    setResetting(true);
    setResetMsg('');
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setResetEmail('');
      showToast(`Password reset email sent to ${resetEmail.trim()}.`);
    } catch (err) {
      setResetMsg('Failed: ' + (err.message ?? 'Unknown error'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="adm-container">

      {toast && (
        <div className="adm-toast">
          <i className="bi bi-check-circle-fill" />{toast}
        </div>
      )}

      <div className="adm-page-header">
        <h1 className="adm-page-title"><i className="bi bi-headset" />Support Actions</h1>
        <p className="adm-page-subtitle">Administrative tools for supporting tenant users — impersonation, resets, and quick actions.</p>
      </div>
      <hr className="adm-divider" />

      <div className="adm-tabs">
        {[
          { key: 'actions',     label: 'Quick Actions',  icon: 'bi-lightning' },
          { key: 'impersonate', label: 'Impersonate',    icon: 'bi-person-badge' },
          { key: 'reset',       label: 'Password Reset', icon: 'bi-key' },
        ].map(t => (
          <button key={t.key} className={`adm-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            <i className={`bi ${t.icon}`} style={{ marginRight: 6 }} />{t.label}
          </button>
        ))}
      </div>

      {/* ── Quick Actions ── */}
      {tab === 'actions' && (
        <div className="adm-grid-3">
          <ActionCard
            icon="bi-arrow-clockwise"
            title="Force Password Reset"
            desc="Send a password reset email to any user in the platform. The action is automatically logged."
            btnLabel="Go to Reset"
            btnVariant="outline"
            onClick={() => setTab('reset')}
          />
          <ActionCard
            icon="bi-person-badge"
            title="Impersonate User"
            desc="Start a read-only session as any platform user for debugging and support. All sessions are audited."
            btnLabel="Go to Impersonate"
            btnVariant="outline"
            onClick={() => setTab('impersonate')}
          />
          <ActionCard
            icon="bi-lock"
            title="Lock Tenant Account"
            desc="Immediately suspend a tenant organization. Their users will be blocked until you re-activate."
            btnLabel="Manage Tenants"
            btnVariant="outline"
            onClick={() => window.location.href = '/admin/tenants'}
          />
          <ActionCard
            icon="bi-envelope-at"
            title="Resend Invitation"
            desc="Resend an onboarding invitation to a user who has not yet accepted or whose link has expired."
            btnLabel="Coming Soon"
            btnVariant="ghost"
            disabled
          />
          <ActionCard
            icon="bi-database-x"
            title="Clear Test Data"
            desc="Remove demo or seeded data from a specific tenant organization before their account goes live."
            btnLabel="Coming Soon"
            btnVariant="ghost"
            disabled
          />
          <ActionCard
            icon="bi-chat-dots"
            title="Send System Notice"
            desc="Broadcast a platform-wide or tenant-specific notice to all active users at their next login."
            btnLabel="Coming Soon"
            btnVariant="ghost"
            disabled
          />
        </div>
      )}

      {/* ── Impersonate ── */}
      {tab === 'impersonate' && (
        <div className="adm-grid-2" style={{ alignItems: 'start' }}>
          <div className="adm-card" style={{ maxWidth: 520 }}>
            <div className="adm-card-header">
              <span className="adm-card-title">Impersonate User Session</span>
            </div>
            <div className="adm-card-body">
              <div className="adm-alert adm-alert--warning">
                <i className="bi bi-exclamation-triangle-fill" />
                All impersonation sessions are recorded in the audit trail. Use only for legitimate support purposes.
              </div>
              {impersonateMsg && <div className="adm-alert adm-alert--danger"><i className="bi bi-x-circle" />{impersonateMsg}</div>}
              <div className="adm-form-group">
                <label className="adm-label">User Email</label>
                <input
                  className="adm-input"
                  type="email"
                  value={impersonateEmail}
                  onChange={e => setImpersonateEmail(e.target.value)}
                  placeholder="user@example.com"
                  list="user-suggestions"
                />
                <datalist id="user-suggestions">
                  {users.map(u => <option key={u.id} value={u.email ?? ''} />)}
                </datalist>
              </div>
              <div className="adm-form-group">
                <label className="adm-label">Reason *</label>
                <textarea
                  className="adm-textarea"
                  value={impersonateReason}
                  onChange={e => setImpersonateReason(e.target.value)}
                  placeholder="Describe the support purpose for this session…"
                />
              </div>
              <button
                className="adm-btn adm-btn--primary"
                onClick={handleImpersonate}
                disabled={impersonating || !impersonateEmail.trim()}
              >
                {impersonating
                  ? <><span className="adm-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Starting…</>
                  : <><i className="bi bi-person-badge" /> Start Session</>}
              </button>
            </div>
          </div>

          <div className="adm-card">
            <div className="adm-card-header">
              <span className="adm-card-title">Platform Users</span>
            </div>
            <div className="adm-card-body--flush">
              {loadingUsers ? (
                <div className="adm-empty" style={{ padding: 30 }}><div className="adm-spinner" /></div>
              ) : users.length === 0 ? (
                <div className="adm-empty" style={{ padding: 30 }}>
                  <i className="bi bi-people" />
                  <span className="adm-empty-text">No users found.</span>
                </div>
              ) : (
                <table className="adm-table">
                  <thead><tr><th>Email</th><th>Role</th><th></th></tr></thead>
                  <tbody>
                    {users.slice(0, 15).map(u => (
                      <tr key={u.id}>
                        <td style={{ fontSize: '0.82rem' }}>{u.email}</td>
                        <td><span className="adm-badge adm-badge--gray" style={{ fontSize: '0.7rem' }}>{u.role ?? 'user'}</span></td>
                        <td>
                          <button
                            className="adm-btn adm-btn--ghost adm-btn--sm"
                            onClick={() => { setImpersonateEmail(u.email ?? ''); setTab('impersonate'); }}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Password Reset ── */}
      {tab === 'reset' && (
        <div className="adm-card" style={{ maxWidth: 480 }}>
          <div className="adm-card-header">
            <span className="adm-card-title">Force Password Reset</span>
          </div>
          <div className="adm-card-body">
            <div className="adm-alert adm-alert--info">
              <i className="bi bi-info-circle" />
              This sends a password reset link via email using Supabase Auth. The user must complete the reset within 24 hours.
            </div>
            {resetMsg && <div className="adm-alert adm-alert--danger"><i className="bi bi-x-circle" />{resetMsg}</div>}
            <div className="adm-form-group">
              <label className="adm-label">User Email *</label>
              <input
                className="adm-input"
                type="email"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <button
              className="adm-btn adm-btn--primary"
              onClick={handlePasswordReset}
              disabled={resetting || !resetEmail.trim()}
            >
              {resetting
                ? <><span className="adm-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Sending…</>
                : <><i className="bi bi-envelope-arrow-up" /> Send Reset Email</>}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SupportActions;
